import { useQuery } from "@tanstack/react-query";
import { getMonth, startOfMonth, subMonths } from "date-fns";
import type React from "react";

import type { EngagedResidentsMonthlyPercentageDto, ProjectEngagementPercentageItemDto } from "@/api/types";
import { BenchmarkMeterChart } from "@/components/Charts/BenchmarkMeterChart";
import { ChartLegend } from "@/components/Charts/ChartLegend";
import { dataColors } from "@/components/Charts/theme";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { Button } from "@/design-system/Button";
import { commonAPIDataSelector } from "@/helpers/Network/selectors";
import { useBool } from "@/hooks/useBool";
import { useSlug } from "@/hooks/useSlug";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { routes } from "@/routes";
import { Trans, useTranslation } from "@/translations";

import { AnalyticsWidgetCard } from "../AnalyticsWidgetCard";
import { SocialContributionWidgetActionsModal } from "./SocialContributionWidgetActionsModal";

const dateNow = new Date();
const FALLBACK_PRIMARY_DATA: Pick<
  ProjectEngagementPercentageItemDto,
  "engagedResidentPercentage" | "engagedResident" | "totalContributionCount" | "range"
> = {
  range: {
    from: startOfMonth(subMonths(dateNow, 1)).toISOString(),
    to: startOfMonth(dateNow).toISOString(),
    month: getMonth(subMonths(dateNow, 1)) + 1,
    year: dateNow.getFullYear(),
  },
  engagedResidentPercentage: 0,
  engagedResident: 0,
  totalContributionCount: 0,
};
const FALLBACK_BENCHMARK_PERCENTAGE = 0.0;
const FALLBACK_PERIOD: EngagedResidentsMonthlyPercentageDto["period"] = "zeroToOneMonth";

export function SocialContributionWidget(): React.ReactNode {
  const [isActionModalOpened, actionModalHandler] = useBool(false);
  const slug = useSlug();
  const { t, i18n } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();

  const {
    data: socialContributionStats,
    isPending,
    isError,
    error,
    refetch,
    isRefetching,
  } = useQuery({
    ...analyticsQueries.contribution("lastMonth"),
    select: commonAPIDataSelector,
  });

  const primaryData = socialContributionStats?.data[0] || FALLBACK_PRIMARY_DATA;
  const benchmarkPercentage =
    socialContributionStats?.benchmark[0].engagedResidentPercentage || FALLBACK_BENCHMARK_PERCENTAGE;
  const period = socialContributionStats?.period || FALLBACK_PERIOD;
  const isDataAvailable = !isPending && !isError && Boolean(socialContributionStats?.data[0]);
  const isActionRequired = isDataAvailable && primaryData.engagedResidentPercentage < benchmarkPercentage;

  return (
    <>
      <AnalyticsWidgetCard.Root
        title={t("component.analytics-widget.social-contribution.title")}
        href={isDataAvailable ? routes.analytics.contributionDetails({ slug }) : undefined}
        isError={isError}
        isLoading={isPending || isRefetching}
        isDataAvailable={isDataAvailable}
        error={error}
        onReload={refetch}
      >
        <AnalyticsWidgetCard.Body>
          <BenchmarkMeterChart
            value={primaryData.engagedResidentPercentage}
            benchmark={benchmarkPercentage}
            isDataAvailable={isDataAvailable}
            ariaLabel="Social contribution chart"
          />
          <ChartLegend
            items={[
              {
                label: (
                  <p className="text-caption">
                    <Trans
                      i18nKey="component.analytics-widget.social-contribution.chart.legend.primary"
                      components={{
                        bold: <span className="text-body-emphasis" />,
                      }}
                      values={{
                        date: formatDate(i18n, "monthYear", primaryData.range.from),
                      }}
                    />
                  </p>
                ),
                color: dataColors.primary,
              },
            ]}
          />
        </AnalyticsWidgetCard.Body>
        <AnalyticsWidgetCard.Footer>
          {isActionRequired && (
            <Button onClick={actionModalHandler.setTrue}>
              {t("component.analytics-widget.social-contribution.action-btn")}
            </Button>
          )}
          <AnalyticsWidgetCard.ReferenceLabel
            currValue={primaryData.engagedResidentPercentage || 0}
            referenceValue={benchmarkPercentage}
          />
          <AnalyticsWidgetCard.BenchmarkInfo benchmark={benchmarkPercentage} period={period} />
        </AnalyticsWidgetCard.Footer>
      </AnalyticsWidgetCard.Root>
      <SocialContributionWidgetActionsModal isOpened={isActionModalOpened} onOpenChange={actionModalHandler.set} />
    </>
  );
}
